import { useCallback, useEffect, useRef, useState } from 'react';
import { PROFILE_STORES_QUEUE_SETTINGS } from '../../../shared/constants/endpoints';
import useApiRequest from '../../../shared/hooks/useApiRequest';
import { logger } from '../../../shared/utils/logger.js';

const DEFAULT_QUEUE_SETTINGS = {
  longestWaitThreshold: '5',
  autoCompleteExpired: 'After 5 minutes',
  requeueAllowed: true,
  priorityPassSurcharge: '100',
  weekdayOpen: '09:00',
  weekdayClosed: '21:00',
  weekendOpen: '10:00',
  weekendClosed: '22:00'
};

const LOG_MODULE = 'useQueueSettings';
const LOG_SOURCE = { file: 'features/hierarchy/hooks/useQueueSettings.js' };

const readQueueSettingsErrorText = (error) => {
  const errorParts = [
    error?.data?.message,
    error?.data?.error,
    error?.data?.raw,
    error?.message
  ];

  return errorParts
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)
    .join(' ')
    .trim();
};

const resolveQueueSettingsMessage = (error, fallback) => {
  const rawMessage = readQueueSettingsErrorText(error);
  if (!rawMessage) {
    return fallback;
  }
  const normalized = rawMessage.toLowerCase();
  if (normalized.includes('no static resource') || normalized.includes('no resource found')) {
    return 'Queue settings service is currently unavailable.';
  }
  if (normalized.includes('unexpected token') || normalized === 'request failed') {
    return fallback;
  }
  return rawMessage;
};

const isMissingQueueSettingsEndpoint = (error) => {
  const text = readQueueSettingsErrorText(error).toLowerCase();
  const status = Number(error?.status || 0);
  return (
    status === 404 ||
    text.includes('no static resource') ||
    text.includes('no resource found') ||
    text.includes('no handler found')
  );
};

const normalizeQueueSettingsPayload = (payload = {}) => ({
  longestWaitThreshold: String(payload?.longestWaitThreshold ?? DEFAULT_QUEUE_SETTINGS.longestWaitThreshold),
  autoCompleteExpired: String(payload?.autoCompleteExpired ?? DEFAULT_QUEUE_SETTINGS.autoCompleteExpired),
  requeueAllowed: payload?.requeueAllowed == null
    ? DEFAULT_QUEUE_SETTINGS.requeueAllowed
    : Boolean(payload.requeueAllowed),
  priorityPassSurcharge: String(payload?.priorityPassSurcharge ?? DEFAULT_QUEUE_SETTINGS.priorityPassSurcharge),
  weekdayOpen: String(payload?.weekdayOpen ?? DEFAULT_QUEUE_SETTINGS.weekdayOpen),
  weekdayClosed: String(payload?.weekdayClosed ?? DEFAULT_QUEUE_SETTINGS.weekdayClosed),
  weekendOpen: String(payload?.weekendOpen ?? DEFAULT_QUEUE_SETTINGS.weekendOpen),
  weekendClosed: String(payload?.weekendClosed ?? DEFAULT_QUEUE_SETTINGS.weekendClosed)
});

const buildQueueControlsPayload = (settings = {}) => ({
  longestWaitThreshold: settings.longestWaitThreshold,
  autoCompleteExpired: settings.autoCompleteExpired,
  requeueAllowed: settings.requeueAllowed,
  priorityPassSurcharge: settings.priorityPassSurcharge,
  weekdayOpen: settings.weekdayOpen,
  weekdayClosed: settings.weekdayClosed,
  weekendOpen: settings.weekendOpen,
  weekendClosed: settings.weekendClosed
});

const extractQueueSettingsPayload = (response) => {
  if (response && typeof response === 'object') {
    if (response.data && typeof response.data === 'object') {
      return response.data;
    }
    return response;
  }
  return {};
};

const useQueueSettings = ({ request, storeToken, onToast } = {}) => {
  const {
    execute,
    error
  } = useApiRequest();
  const [queueSettings, setQueueSettings] = useState(DEFAULT_QUEUE_SETTINGS);
  const [isSettingsSaving, setIsSettingsSaving] = useState(false);
  const queueSettingsRef = useRef(DEFAULT_QUEUE_SETTINGS);
  const onToastRef = useRef(onToast);
  const fetchInFlightRef = useRef(null);

  useEffect(() => {
    queueSettingsRef.current = queueSettings;
  }, [queueSettings]);

  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  const handleQueueSettingChange = useCallback((field, value) => {
    setQueueSettings((previous) => ({ ...previous, [field]: value }));
  }, []);

  const requestQueueSettings = useCallback(async ({
    method,
    requestName,
    body
  }) => {
    if (typeof request !== 'function') {
      throw new Error('Request client is unavailable for queue settings.');
    }
    if (!storeToken) {
      throw new Error('Store session is missing for queue settings.');
    }

    try {
      return await request(PROFILE_STORES_QUEUE_SETTINGS, {
        method,
        headers: {
          ...(body ? { 'Content-Type': 'application/json' } : {})
        },
        ...(body ? { body } : {}),
        requestName
      });
    } catch (error) {
      if (isMissingQueueSettingsEndpoint(error)) {
        logger.warn(LOG_MODULE, 'Queue settings endpoint unavailable', {
          source: { ...LOG_SOURCE, method: 'requestQueueSettings' },
          endpoint: PROFILE_STORES_QUEUE_SETTINGS,
          requestName
        });
      }
      throw error;
    }
  }, [request, storeToken]);

  const fetchQueueSettings = useCallback(async () => {
    if (fetchInFlightRef.current) {
      return fetchInFlightRef.current;
    }

    const loadPromise = execute(() => requestQueueSettings({
        method: 'GET',
        requestName: 'queue-settings-get'
      }), {
        logModule: 'useQueueSettings.fetchQueueSettings',
        onSuccess: (response) => {
          const normalized = normalizeQueueSettingsPayload(extractQueueSettingsPayload(response));
          setQueueSettings(normalized);
        },
        onError: (requestError) => {
          const message = resolveQueueSettingsMessage(requestError, 'Unable to load queue settings.');
          logger.error(LOG_MODULE, 'Failed to fetch queue settings', {
            source: { ...LOG_SOURCE, method: 'fetchQueueSettings' },
            message
          });
          onToastRef.current?.({ message, variant: 'error' });
        }
      })
      .finally(() => {
        fetchInFlightRef.current = null;
      });

    fetchInFlightRef.current = loadPromise;
    return loadPromise;
  }, [execute, requestQueueSettings]);

  const saveQueueSettings = useCallback(async () => {
    setIsSettingsSaving(true);
    try {
      return await execute(() => requestQueueSettings({
        method: 'PUT',
        requestName: 'queue-settings-save',
        body: JSON.stringify(buildQueueControlsPayload(queueSettingsRef.current))
      }), {
        logModule: 'useQueueSettings.saveQueueSettings',
        onSuccess: (response) => {
          const normalized = normalizeQueueSettingsPayload(extractQueueSettingsPayload(response) || queueSettingsRef.current);
          setQueueSettings(normalized);
          onToastRef.current?.({ message: 'Settings saved successfully.', variant: 'success' });
        },
        onError: (requestError) => {
          const message = resolveQueueSettingsMessage(requestError, 'Unable to save queue settings.');
          logger.error(LOG_MODULE, 'Failed to save queue settings', {
            source: { ...LOG_SOURCE, method: 'saveQueueSettings' },
            message
          });
          onToastRef.current?.({ message, variant: 'error' });
        }
      });
    } finally {
      setIsSettingsSaving(false);
    }
  }, [execute, requestQueueSettings]);

  return {
    queueSettings,
    handleQueueSettingChange,
    fetchQueueSettings,
    saveQueueSettings,
    isSettingsSaving,
    settingsError: error
  };
};

export default useQueueSettings;
